import { Suspense, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import SpeakingAvatar from './three/SpeakingAvatar'
import SafeCanvas from './three/SafeCanvas'
import { useNarration } from '../audio/NarrationContext'

/**
 * A circular profile-portrait porthole — the character cut at FACE level
 * (head + shoulders only), tilted, popping out of a different corner for
 * every section. Narration starts for a section only once its top edge
 * has reached the top of the screen (fully arrived) and the scroll has
 * settled — never mid-fling, never on first pixel.
 */
const SPOTS = {
  home: { side: 'right', vertical: 'bottom', variant: 'popUp', tilt: -7 },
  experience: { side: 'right', vertical: 'bottom', variant: 'popUp', tilt: -7 },
  skills: { side: 'left', vertical: 'bottom', variant: 'popUp', tilt: 7 },
  projects: { side: 'right', vertical: 'top', variant: 'drop', tilt: 7 },
  gallery: { side: 'left', vertical: 'top', variant: 'drop', tilt: -7 },
  story: { side: 'right', vertical: 'bottom', variant: 'slide', tilt: -8 },
  contact: { side: 'left', vertical: 'bottom', variant: 'popUp', tilt: 7 },
}

const ENTRANCES = {
  popUp: {
    initial: { y: 260, opacity: 0 },
    transition: { type: 'spring', stiffness: 240, damping: 15 },
  },
  drop: {
    initial: { y: -260, opacity: 0 },
    transition: { type: 'spring', stiffness: 240, damping: 15 },
  },
  slide: {
    initial: { x: 280, y: 50, opacity: 0 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
}

export default function FloatingCompanion({ active }) {
  const [reduced, setReduced] = useState(false)
  const [modelMissing, setModelMissing] = useState(false)
  const [settledTick, setSettledTick] = useState(0)
  const fastRef = useRef(false)
  const { play, current, playing, canAutoPlay, autoSuppressedFor, endedFor } = useNarration()
  const speaking = playing && current !== null

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onMq = () => setReduced(mq.matches)
    onMq()
    mq.addEventListener('change', onMq)
    return () => mq.removeEventListener('change', onMq)
  }, [])

  // fast-scroll watcher: skip auto-starts while the visitor is flinging
  useEffect(() => {
    let lastY = window.scrollY
    let lastT = performance.now()
    let timer
    const onScroll = () => {
      const now = performance.now()
      const y = window.scrollY
      fastRef.current = Math.abs(y - lastY) / Math.max(16, now - lastT) > 1.1
      lastY = y
      lastT = now
      clearTimeout(timer)
      timer = setTimeout(() => {
        fastRef.current = false
        setSettledTick((t) => t + 1) // scroll settled — re-evaluate auto-play
      }, 500)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(timer)
    }
  }, [])

  // AUTO: every section narrates, but only when it has FULLY arrived —
  // its top edge touching the top of the viewport — and the scroll has
  // settled (never on first pixel, never mid-fling)
  useEffect(() => {
    if (reduced) return
    if (current === active && playing) return
    if (autoSuppressedFor() === active) return
    if (endedFor() === active) return
    const t = setTimeout(() => {
      if (fastRef.current || !canAutoPlay()) return
      const el = document.getElementById(active)
      const top = el ? el.getBoundingClientRect().top : Infinity
      if (active !== 'home' && top > 160) return // not fully on screen yet
      play(active)
    }, 750)
    return () => clearTimeout(t)
  }, [active, settledTick, reduced, current, playing, play, canAutoPlay, autoSuppressedFor, endedFor])

  // visible only while actually speaking (never on top of the hero model)
  const onHome = active === 'home' && current !== 'home'
  const spot = SPOTS[current] || SPOTS.story
  const entrance = ENTRANCES[spot.variant]
  const visible = speaking && !reduced && !onHome

  const posStyle = { [spot.side]: '-0.75rem' }
  if (spot.vertical === 'top') posStyle.top = '5rem'
  else posStyle.bottom = '5vh'

  return (
    <div className="pointer-events-none fixed inset-0 z-[35] overflow-hidden" aria-hidden="true">
      <AnimatePresence>
        {visible && (
          <motion.div
            key={current}
            data-testid="floating-character"
            initial={entrance.initial}
            animate={{ x: 0, y: 0, opacity: 1, rotate: spot.tilt }}
            exit={{ opacity: 0, scale: 0.7, y: 50, transition: { duration: 0.3 } }}
            transition={entrance.transition}
            className="absolute h-[170px] w-[170px] md:h-[240px] md:w-[240px]"
            style={posStyle}
          >
            {/* the half-circle porthole he pops out of */}
            <div
              data-testid="character-porthole"
              className="relative h-full w-full overflow-hidden rounded-full border-2 border-white/20 bg-black/45 shadow-[0_18px_50px_rgba(0,0,0,0.5)] backdrop-blur-sm"
            >
              {modelMissing ? (
                <div className="font-display flex h-full w-full items-center justify-center text-4xl font-semibold text-white/85">
                  VK
                </div>
              ) : (
                <SafeCanvas
                  canvasProps={{
                    dpr: [1, 1.5],
                    camera: { position: [0, 0.12, 0.85], fov: 32 },
                    gl: { alpha: true, antialias: true },
                  }}
                >
                  <Suspense fallback={null}>
                    <ambientLight intensity={0.8} />
                    <directionalLight position={[2, 3, 4]} intensity={1.3} />
                    <directionalLight position={[-3, 1, 2]} intensity={0.5} />
                    <SpeakingAvatar
                      speaking
                      position={[0, -1.58, 0]}
                      onMissing={() => setModelMissing(true)}
                    />
                  </Suspense>
                </SafeCanvas>
              )}
              <span className="absolute bottom-3 left-1/2 z-10 h-2 w-2 -translate-x-1/2 animate-pulse rounded-full bg-[var(--hero-orange)]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
